import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Billboard, Text } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import toast from 'react-hot-toast';
import {
  ItemAlmacen3D,
  CapacidadRackInfo,
  ConfiguracionAlmacen3D,
  RackSeleccionadoData,
  RackLayoutData,
  SlotInfo,
} from './tipos';
import { EscenarioAlmacen } from './EscenarioAlmacen';
import { Rack3D } from './Rack3D';
import { PanelEditorArquitecto } from './PanelEditorArquitecto';
import { InspectorRackSeleccionado } from './InspectorRackSeleccionado';
import { ModalCrearRack } from './ModalCrearRack';
import { ModalGestionSlot } from './ModalGestionSlot';
import { TruckIcon } from '@heroicons/react/24/outline';

interface AlmacenDigitalTwin3DProps {
  zonasAlmacen: Record<string, Record<string, Record<string, ItemAlmacen3D[]>>>;
  configuracionAlmacen: ConfiguracionAlmacen3D;
  obtenerCapacidadRack: (data?: {
    zonaOperativa?: string;
    pasillo?: string;
    rack?: string;
  }) => CapacidadRackInfo;
  formatearCodigoUbicacion: (
    data: {
      zonaOperativa?: string;
      pasillo?: string;
      rack?: string;
      nivel?: string;
      posicion?: string;
    },
    opts?: { incluirNivel?: boolean; incluirPosicion?: boolean; formato?: 'compacto' | 'visual' }
  ) => string;
  obtenerNombreZona: (codigo: string) => string;
  onSelectRack: (data: RackSeleccionadoData) => void;
  modoArquitectoInicial?: boolean;
  onGuardarConfiguracion?: (nuevoLayoutData: Record<string, any>) => Promise<void> | void;
  onMoverProductosSlot?: (
    items: ItemAlmacen3D[],
    destino: { zona: string; pasillo: string; rack: string; nivel: number; posicion: number }
  ) => Promise<void> | void;
  onAjustarStockItem?: (itemId: string, nuevoStock: number) => Promise<void> | void;
  onDespacharSlot?: (items: ItemAlmacen3D[]) => Promise<void> | void;
}

export const AlmacenDigitalTwin3D: React.FC<AlmacenDigitalTwin3DProps> = ({
  zonasAlmacen,
  configuracionAlmacen,
  obtenerCapacidadRack,
  formatearCodigoUbicacion,
  obtenerNombreZona,
  onSelectRack,
  modoArquitectoInicial = false,
  onGuardarConfiguracion,
  onMoverProductosSlot,
  onAjustarStockItem,
  onDespacharSlot,
}) => {
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const [vistaActual, setVistaActual] = useState<'isometrica' | 'cenital' | 'suelo'>('isometrica');

  // Estado del Modo Arquitecto (Editor 3D FASE 2)
  const [modoArquitecto, setModoArquitecto] = useState<boolean>(modoArquitectoInicial);
  const [pasoGrid, setPasoGrid] = useState<number>(1.0);
  const [rackSeleccionadoId, setRackSeleccionadoId] = useState<string | null>(null);
  const [modalCrearAbierto, setModalCrearAbierto] = useState<boolean>(false);
  const [guardando, setGuardando] = useState<boolean>(false);

  // Estado de FASE 3: Logística Granular (Slots & Pallets)
  const [slotSeleccionado, setSlotSeleccionado] = useState<SlotInfo | null>(null);
  const [slotOrigenReubicacion, setSlotOrigenReubicacion] = useState<SlotInfo | null>(null);

  // Inicializar distribución de racks (Layout Paramétrico + Persistencia 3D)
  const [racksLayout, setRacksLayout] = useState<Record<string, RackLayoutData>>(() => {
    const saved = configuracionAlmacen.layout_data?.racks3d as Record<string, RackLayoutData> | undefined;
    const initial: Record<string, RackLayoutData> = {};
    const zonaEntries = Object.entries(zonasAlmacen);
    const totalZonas = zonaEntries.length;
    const zonaWidthEstimate = 26;
    const rackSpacingX = 4.6;
    const pasilloSpacingZ = 6.8;

    zonaEntries.forEach(([zonaKey, pasillos], zIdx) => {
      const baseX = (zIdx - (totalZonas - 1) / 2) * zonaWidthEstimate;
      const pasilloEntries = Object.entries(pasillos);

      pasilloEntries.forEach(([pasilloKey, racks], pIdx) => {
        const pasilloZ = (pIdx - (pasilloEntries.length - 1) / 2) * pasilloSpacingZ;
        const rackEntries = Object.entries(racks);
        const totalRacks = rackEntries.length;

        rackEntries.forEach(([rackKey], rIdx) => {
          const id = `${zonaKey}-${pasilloKey}-${rackKey}`;
          const posX = baseX + (rIdx - (totalRacks - 1) / 2) * rackSpacingX;
          const posZ = pasilloZ;
          const cap = obtenerCapacidadRack({
            zonaOperativa: zonaKey,
            pasillo: pasilloKey,
            rack: rackKey,
          });

          if (saved && saved[id]) {
            initial[id] = {
              ...saved[id],
              id,
              zona: zonaKey,
              pasillo: pasilloKey,
              rack: rackKey,
            };
          } else {
            initial[id] = {
              id,
              zona: zonaKey,
              pasillo: pasilloKey,
              rack: rackKey,
              x: Number(posX.toFixed(1)),
              z: Number(posZ.toFixed(1)),
              rotacion: 0,
              niveles: cap.niveles || 3,
              ancho: 3.8,
              max_ton: cap.max_ton || 3.5,
              max_unidades: cap.max_unidades || 48,
            };
          }
        });
      });
    });

    // Racks adicionales guardados que no tengan stock aún
    if (saved) {
      Object.entries(saved).forEach(([id, r]) => {
        if (!initial[id]) {
          initial[id] = r;
        }
      });
    }

    return initial;
  });

  // Estadísticas globales para el HUD
  const stats = useMemo(() => {
    let itemsCount = 0;
    let pesoTotalKg = 0;

    Object.values(zonasAlmacen).forEach((pasillos) => {
      Object.values(pasillos).forEach((racks) => {
        Object.values(racks).forEach((items) => {
          items.forEach((it) => {
            itemsCount += Number(it.cantidad) || 0;
            pesoTotalKg += (Number(it.peso) || 0) * (Number(it.cantidad) || 0);
          });
        });
      });
    });

    return {
      zonas: Object.keys(zonasAlmacen).length,
      racks: Object.keys(racksLayout).length,
      totalUnidades: itemsCount,
      pesoTotalTon: (pesoTotalKg / 1000).toFixed(2),
    };
  }, [zonasAlmacen, racksLayout]);

  // Lista de zonas y pasillos disponibles para el modal de creación
  const zonasDisponibles = useMemo(() => {
    const s = new Set<string>();
    Object.keys(zonasAlmacen).forEach((z) => s.add(z));
    Object.values(racksLayout).forEach((r) => s.add(r.zona));
    return Array.from(s);
  }, [zonasAlmacen, racksLayout]);

  const pasillosDisponibles = useMemo(() => {
    const s = new Set<string>();
    Object.values(zonasAlmacen).forEach((pMap) => {
      Object.keys(pMap).forEach((p) => s.add(p));
    });
    Object.values(racksLayout).forEach((r) => s.add(r.pasillo));
    return Array.from(s);
  }, [zonasAlmacen, racksLayout]);

  // Rack actualmente seleccionado en el editor
  const rackSeleccionado = useMemo(() => {
    if (!rackSeleccionadoId) return null;
    return racksLayout[rackSeleccionadoId] || null;
  }, [rackSeleccionadoId, racksLayout]);

  // Controladores de cámara rápida
  const aplicarVista = (tipo: 'isometrica' | 'cenital' | 'suelo') => {
    if (!controlsRef.current) return;
    setVistaActual(tipo);

    const controls = controlsRef.current;
    const camera = controls.object;

    if (tipo === 'isometrica') {
      camera.position.set(24, 20, 24);
      controls.target.set(0, 1, 0);
    } else if (tipo === 'cenital') {
      camera.position.set(0, 38, 0.01);
      controls.target.set(0, 0, 0);
    } else if (tipo === 'suelo') {
      camera.position.set(0, 1.8, 16);
      controls.target.set(0, 1.8, 0);
    }
    controls.update();
  };

  // Acciones del Editor FASE 2:
  const handleMoverRack = useCallback(
    (dx: number, dz: number) => {
      if (!rackSeleccionadoId) return;
      setRacksLayout((prev) => {
        const actual = prev[rackSeleccionadoId];
        if (!actual) return prev;
        const nuevoX = Number((Math.round((actual.x + dx) / pasoGrid) * pasoGrid).toFixed(2));
        const nuevoZ = Number((Math.round((actual.z + dz) / pasoGrid) * pasoGrid).toFixed(2));
        return {
          ...prev,
          [rackSeleccionadoId]: {
            ...actual,
            x: nuevoX,
            z: nuevoZ,
          },
        };
      });
    },
    [rackSeleccionadoId, pasoGrid]
  );

  const handleRotarRack = useCallback(
    (deltaGrados: number) => {
      if (!rackSeleccionadoId) return;
      setRacksLayout((prev) => {
        const actual = prev[rackSeleccionadoId];
        if (!actual) return prev;
        const nuevaRotacion = (actual.rotacion + deltaGrados + 360) % 360;
        return {
          ...prev,
          [rackSeleccionadoId]: {
            ...actual,
            rotacion: nuevaRotacion,
          },
        };
      });
    },
    [rackSeleccionadoId]
  );

  const handleCambiarDimensiones = useCallback(
    (updates: Partial<RackLayoutData>) => {
      if (!rackSeleccionadoId) return;
      setRacksLayout((prev) => {
        const actual = prev[rackSeleccionadoId];
        if (!actual) return prev;
        return {
          ...prev,
          [rackSeleccionadoId]: {
            ...actual,
            ...updates,
          },
        };
      });
    },
    [rackSeleccionadoId]
  );

  const handleCrearRack = useCallback((nuevoRack: Omit<RackLayoutData, 'id'>) => {
    const id = `${nuevoRack.zona}-${nuevoRack.pasillo}-${nuevoRack.rack}_${Date.now().toString().slice(-4)}`;
    const rackCreado: RackLayoutData = {
      ...nuevoRack,
      id,
    };
    setRacksLayout((prev) => ({
      ...prev,
      [id]: rackCreado,
    }));
    setRackSeleccionadoId(id);
    toast.success(`Rack ${nuevoRack.rack} instalado con éxito`);
  }, []);

  const handleDuplicarRack = useCallback(() => {
    if (!rackSeleccionado) return;
    const matchNum = rackSeleccionado.rack.match(/\d+/);
    const nextNum = matchNum ? parseInt(matchNum[0], 10) + 1 : Date.now().toString().slice(-2);
    const nuevoCodigo = matchNum
      ? rackSeleccionado.rack.replace(/\d+/, String(nextNum).padStart(matchNum[0].length, '0'))
      : `${rackSeleccionado.rack} (Copia)`;

    const id = `${rackSeleccionado.zona}-${rackSeleccionado.pasillo}-${nuevoCodigo}_${Date.now().toString().slice(-4)}`;
    const clon: RackLayoutData = {
      ...rackSeleccionado,
      id,
      rack: nuevoCodigo,
      x: Number((rackSeleccionado.x + 3.4).toFixed(1)),
      z: rackSeleccionado.z,
    };

    setRacksLayout((prev) => ({
      ...prev,
      [id]: clon,
    }));
    setRackSeleccionadoId(id);
    toast.success(`Rack duplicado como ${nuevoCodigo}`);
  }, [rackSeleccionado]);

  const handleEliminarRack = useCallback(() => {
    if (!rackSeleccionadoId || !rackSeleccionado) return;
    const confirmacion = window.confirm(
      `¿Confirmas la eliminación del ${rackSeleccionado.rack}? Esta acción retirará la bahía de la distribución.`
    );
    if (!confirmacion) return;

    setRacksLayout((prev) => {
      const copy = { ...prev };
      delete copy[rackSeleccionadoId];
      return copy;
    });
    setRackSeleccionadoId(null);
    toast.success('Rack eliminado de la planta 3D');
  }, [rackSeleccionadoId, rackSeleccionado]);

  const handleGuardarLayout = async () => {
    setGuardando(true);
    try {
      const nuevoLayout = {
        ...(configuracionAlmacen.layout_data || {}),
        racks3d: racksLayout,
      };

      if (onGuardarConfiguracion) {
        await onGuardarConfiguracion(nuevoLayout);
      }
      toast.success('¡Distribución del almacén 3D guardada exitosamente!');
    } catch (err) {
      console.error(err);
      toast.error('No se pudo guardar la distribución');
    } finally {
      setGuardando(false);
    }
  };

  const handleResetLayout = () => {
    if (window.confirm('¿Deseas restaurar la distribución original calculada por el sistema?')) {
      const saved = configuracionAlmacen.layout_data?.racks3d;
      if (saved) {
        setRacksLayout(saved);
      } else {
        window.location.reload();
      }
      setRackSeleccionadoId(null);
      toast('Distribución restaurada');
    }
  };

  // Operaciones FASE 3: Logística
  // Reubicación de Pallet de un slot a otro
  const handleEjecutarReubicacion = async (origen: SlotInfo, destino: SlotInfo) => {
    try {
      const itemsAMover = origen.items;
      if (!itemsAMover.length) {
        setSlotOrigenReubicacion(null);
        return;
      }

      // Validación de Capacidad Estructural (Tolerancia en Toneladas)
      const capDestino = obtenerCapacidadRack({
        zonaOperativa: destino.zona,
        pasillo: destino.pasillo,
        rack: destino.rack,
      });

      if (origen.pesoTotalTon > (capDestino.max_ton || 5)) {
        toast.error(
          `¡Alerta estructural! La carga del pallet (${origen.pesoTotalTon.toFixed(2)}t) supera la tolerancia del rack destino (${capDestino.max_ton}t).`
        );
        return;
      }

      if (onMoverProductosSlot) {
        await onMoverProductosSlot(itemsAMover, {
          zona: destino.zona,
          pasillo: destino.pasillo,
          rack: destino.rack,
          nivel: destino.nivel,
          posicion: destino.posicion,
        });
      }

      toast.success(
        `¡Pallet ${origen.lpn} reubicado con éxito en ${destino.zona}-${destino.pasillo}-${destino.rack} (Nivel ${destino.nivel}, Celda S${destino.posicion})!`,
        { icon: '📦' }
      );
    } catch (err) {
      console.error(err);
      toast.error('Error al reubicar el pallet');
    } finally {
      setSlotOrigenReubicacion(null);
    }
  };

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }

      if (e.key === 'Escape') {
        if (slotOrigenReubicacion) {
          setSlotOrigenReubicacion(null);
          toast('Modo reubicación cancelado');
        } else if (slotSeleccionado) {
          setSlotSeleccionado(null);
        } else if (rackSeleccionadoId) {
          setRackSeleccionadoId(null);
        }
      } else if (modoArquitecto && rackSeleccionadoId) {
        if (e.key.toLowerCase() === 'r') {
          e.preventDefault();
          handleRotarRack(90);
        } else if (e.key.toLowerCase() === 'd' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          handleDuplicarRack();
        } else if (e.key === 'Delete') {
          e.preventDefault();
          handleEliminarRack();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          handleMoverRack(0, -pasoGrid);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          handleMoverRack(0, pasoGrid);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          handleMoverRack(-pasoGrid, 0);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          handleMoverRack(pasoGrid, 0);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    modoArquitecto,
    rackSeleccionadoId,
    pasoGrid,
    slotOrigenReubicacion,
    slotSeleccionado,
    handleRotarRack,
    handleDuplicarRack,
    handleEliminarRack,
    handleMoverRack,
  ]);

  // Dimensiones dinámicas de la nave
  const rackPositions = Object.values(racksLayout);
  const minX = rackPositions.length ? Math.min(...rackPositions.map((r) => r.x)) : -20;
  const maxX = rackPositions.length ? Math.max(...rackPositions.map((r) => r.x)) : 20;
  const minZ = rackPositions.length ? Math.min(...rackPositions.map((r) => r.z)) : -20;
  const maxZ = rackPositions.length ? Math.max(...rackPositions.map((r) => r.z)) : 20;

  const anchoNave = Math.max(55, maxX - minX + 20, (configuracionAlmacen.dimension_ancho || 50) * 0.8);
  const largoNave = Math.max(55, maxZ - minZ + 20, (configuracionAlmacen.dimension_largo || 50) * 0.8);

  return (
    <div className="flex-1 w-full h-full relative overflow-hidden bg-slate-950 select-none">
      {/* 1. HUD SUPERIOR IZQUIERDO: Control de perspectivas y cámaras */}
      <div className="absolute top-4 left-4 z-20 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-slate-900/85 backdrop-blur-xl p-1 rounded-xl border border-slate-700/60 shadow-xl">
          <button
            onClick={() => aplicarVista('isometrica')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              vistaActual === 'isometrica'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            📐 Isométrica
          </button>
          <button
            onClick={() => aplicarVista('cenital')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              vistaActual === 'cenital'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            🗺️ Cenital (Planta)
          </button>
          <button
            onClick={() => aplicarVista('suelo')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              vistaActual === 'suelo'
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            🚶 Nivel Suelo
          </button>
          <div className="w-px h-5 bg-slate-700 mx-1" />
          <button
            onClick={() => aplicarVista('isometrica')}
            title="Centrar y reajustar cámara"
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all text-xs"
          >
            🎯 Centrar
          </button>
        </div>

        {/* Interruptor Modo Arquitecto 3D */}
        <button
          onClick={() => {
            setModoArquitecto(!modoArquitecto);
            setRackSeleccionadoId(null);
            setSlotOrigenReubicacion(null);
            setSlotSeleccionado(null);
          }}
          className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-extrabold shadow-lg transition-all border ${
            modoArquitecto
              ? 'bg-gradient-to-r from-cyan-600 to-blue-600 border-cyan-400 text-white ring-2 ring-cyan-500/40 animate-pulse'
              : 'bg-slate-900/90 text-cyan-400 border-cyan-500/30 hover:bg-slate-800'
          }`}
        >
          <span>{modoArquitecto ? '🛠️ Editor Activo' : '📐 Activar Modo Arquitecto'}</span>
        </button>
      </div>

      {/* 2. BANNER DE REUBICACIÓN EN TIEMPO REAL */}
      {slotOrigenReubicacion && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 bg-amber-500 text-slate-950 px-5 py-2.5 rounded-2xl shadow-2xl font-bold text-xs animate-bounce border border-amber-300">
          <TruckIcon className="w-5 h-5 text-slate-950" />
          <span>
            Reubicación Activa: Haz clic en un hueco libre para colocar el Pallet {slotOrigenReubicacion.lpn} ({slotOrigenReubicacion.slotKey})
          </span>
          <button
            onClick={() => setSlotOrigenReubicacion(null)}
            className="ml-2 px-2.5 py-1 bg-slate-950 text-white rounded-xl text-[10px] uppercase font-black tracking-wider hover:bg-slate-800 transition-all"
          >
            Cancelar (Esc)
          </button>
        </div>
      )}

      {/* 3. BARRA DE HERRAMIENTAS MODO ARQUITECTO (Top Center) */}
      {modoArquitecto && (
        <PanelEditorArquitecto
          onAbrirCrearRack={() => setModalCrearAbierto(true)}
          pasoGrid={pasoGrid}
          onCambiarPasoGrid={setPasoGrid}
          onGuardarLayout={handleGuardarLayout}
          onResetLayout={handleResetLayout}
          onSalirModoArquitecto={() => {
            setModoArquitecto(false);
            setRackSeleccionadoId(null);
          }}
          guardando={guardando}
          totalRacks={Object.keys(racksLayout).length}
        />
      )}

      {/* 4. INSPECTOR FLOTANTE DEL RACK SELECCIONADO (Bottom Right) */}
      {modoArquitecto && rackSeleccionado && (
        <InspectorRackSeleccionado
          rack={rackSeleccionado}
          pasoGrid={pasoGrid}
          onMover={handleMoverRack}
          onRotar={handleRotarRack}
          onCambiarDimensiones={handleCambiarDimensiones}
          onDuplicar={handleDuplicarRack}
          onEliminar={handleEliminarRack}
          onDeseleccionar={() => setRackSeleccionadoId(null)}
        />
      )}

      {/* 5. MODAL CREAR RACK */}
      <ModalCrearRack
        isOpen={modalCrearAbierto}
        onClose={() => setModalCrearAbierto(false)}
        zonasDisponibles={zonasDisponibles}
        pasillosDisponibles={pasillosDisponibles}
        onCrear={handleCrearRack}
      />

      {/* 6. MODAL GESTIÓN DE SLOT Y PALLET (FASE 3) */}
      <ModalGestionSlot
        slot={slotSeleccionado}
        isOpen={Boolean(slotSeleccionado)}
        onClose={() => setSlotSeleccionado(null)}
        onIniciarReubicacion={(s) => {
          setSlotOrigenReubicacion(s);
          toast('Haz clic en el hueco libre de destino en el 3D', { icon: '🚚' });
        }}
        onVerRackCompleto={(s) => {
          const rackData = Object.values(racksLayout).find(
            (r) =>
              r.zona.toUpperCase() === s.zona.toUpperCase() &&
              r.pasillo.replace(/\D/g, '') === s.pasillo.replace(/\D/g, '') &&
              r.rack.replace(/\D/g, '') === s.rack.replace(/\D/g, '')
          );
          let itemsRack = zonasAlmacen[s.zona]?.[s.pasillo]?.[s.rack] || [];
          if (!itemsRack.length) {
            const zEntry = Object.entries(zonasAlmacen).find(([z]) => z.toUpperCase() === s.zona.toUpperCase());
            if (zEntry) {
              for (const [p, rMap] of Object.entries(zEntry[1])) {
                if (p.replace(/\D/g, '') === s.pasillo.replace(/\D/g, '')) {
                  for (const [r, itList] of Object.entries(rMap)) {
                    if (r.replace(/\D/g, '') === s.rack.replace(/\D/g, '')) {
                      itemsRack = itList;
                      break;
                    }
                  }
                }
              }
            }
          }
          const codigoVisual = formatearCodigoUbicacion(
            {
              zonaOperativa: s.zona,
              pasillo: s.pasillo,
              rack: s.rack,
            },
            {
              incluirNivel: false,
              incluirPosicion: false,
              formato: 'visual',
            }
          );
          onSelectRack({
            nombre: codigoVisual,
            zona: s.zona,
            pasillo: s.pasillo,
            rack: s.rack,
            items: itemsRack,
            niveles: rackData?.niveles,
            ancho: rackData?.ancho,
          });
        }}
        onAjustarStock={async (s, itemId, delta) => {
          const item = s.items.find((i) => i.id === itemId);
          if (!item) return;
          const nuevoStock = Math.max(1, item.cantidad + delta);
          if (onAjustarStockItem) {
            await onAjustarStockItem(itemId, nuevoStock);
          }
          item.cantidad = nuevoStock;
          setSlotSeleccionado({ ...s });
        }}
        onDespacharPallet={async (s) => {
          if (onDespacharSlot) {
            await onDespacharSlot(s.items);
          }
          setSlotSeleccionado(null);
        }}
      />

      {/* 7. HUD SUPERIOR DERECHO: Resumen de Telemetría WMS */}
      <div className="absolute top-4 right-4 z-20 hidden md:flex items-center gap-3 bg-slate-900/85 backdrop-blur-xl px-4 py-2 rounded-xl border border-slate-700/60 shadow-xl text-xs">
        <div className="flex flex-col items-center">
          <span className="text-[10px] uppercase font-bold text-slate-400">Zonas</span>
          <span className="font-extrabold text-white text-sm">{stats.zonas}</span>
        </div>
        <div className="w-px h-7 bg-slate-700" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] uppercase font-bold text-slate-400">Racks</span>
          <span className="font-extrabold text-blue-400 text-sm">{stats.racks}</span>
        </div>
        <div className="w-px h-7 bg-slate-700" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] uppercase font-bold text-slate-400">Unidades</span>
          <span className="font-extrabold text-amber-400 text-sm">{stats.totalUnidades} u</span>
        </div>
        <div className="w-px h-7 bg-slate-700" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] uppercase font-bold text-slate-400">Carga Total</span>
          <span className="font-extrabold text-emerald-400 text-sm">{stats.pesoTotalTon} t</span>
        </div>
      </div>

      {/* 8. HUD INFERIOR: Leyenda WMS e Instrucciones */}
      {!modoArquitecto && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex flex-wrap items-center justify-center gap-3 bg-slate-900/90 backdrop-blur-xl px-5 py-2.5 rounded-full border border-slate-700/60 shadow-2xl text-[11px] font-semibold text-slate-300">
          <div className="flex items-center gap-2 pr-3 border-r border-slate-700">
            <span className="text-slate-400">Interacción:</span>
            <span className="flex items-center gap-1 text-slate-200">👆 Clic en Pallet: Gestionar LPN</span>
            <span className="flex items-center gap-1 text-slate-200">🔍 Scroll: Zoom</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"></span>
              <span>Disponible</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600 shadow-[0_0_8px_rgba(220,38,38,0.7)]"></span>
              <span>Cuarentena / Dañado</span>
            </div>
          </div>
        </div>
      )}

      {/* 9. CANVAS PRINCIPAL DE THREE.JS / REACT THREE FIBER */}
      <Canvas
        shadows
        camera={{ position: [24, 20, 24], fov: 46 }}
        gl={{
          antialias: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.15,
        }}
        onPointerDown={(e) => {
          if (modoArquitecto && e.target === e.currentTarget) {
            setRackSeleccionadoId(null);
          }
        }}
        onCreated={({ gl }) => {
          gl.setClearColor('#030712');
        }}
      >
        <OrbitControls
          ref={controlsRef}
          makeDefault
          enableDamping
          dampingFactor={0.06}
          maxPolarAngle={Math.PI / 2 - 0.03}
          minDistance={4}
          maxDistance={95}
        />

        {/* Entorno Físico Industrial */}
        <EscenarioAlmacen
          configuracion={configuracionAlmacen}
          ancho={anchoNave}
          largo={largoNave}
        />

        {/* Señalética flotante de Zonas Operativas */}
        {zonasDisponibles.map((zonaKey) => {
          const racksDeZona = Object.values(racksLayout).filter((r) => r.zona === zonaKey);
          if (!racksDeZona.length) return null;
          const avgX = racksDeZona.reduce((sum, r) => sum + r.x, 0) / racksDeZona.length;
          const minZZona = Math.min(...racksDeZona.map((r) => r.z));
          const nombre = obtenerNombreZona(zonaKey);

          return (
            <Billboard key={`badge-zona-${zonaKey}`} position={[avgX, 7.5, minZZona - 3]}>
              <mesh position={[0, 0, -0.01]}>
                <planeGeometry args={[4.4, 0.75]} />
                <meshBasicMaterial color="#eab308" />
              </mesh>
              <Text
                fontSize={0.28}
                color="#0f172a"
                anchorX="center"
                anchorY="middle"
                fontWeight="bold"
              >
                {`ZONA: ${nombre.toUpperCase()} (${zonaKey})`}
              </Text>
            </Billboard>
          );
        })}

        {/* Racks 3D en planta con granularidad de slots y pallets */}
        <group>
          {Object.values(racksLayout).map((rackData) => {
            let itemsRack = zonasAlmacen[rackData.zona]?.[rackData.pasillo]?.[rackData.rack] || [];
            if (!itemsRack.length) {
              const pasNum = rackData.pasillo.replace(/\D/g, '');
              const racNum = rackData.rack.replace(/\D/g, '');
              const zEntry = Object.entries(zonasAlmacen).find(([z]) => z.toUpperCase() === rackData.zona.toUpperCase());
              if (zEntry) {
                for (const [p, rMap] of Object.entries(zEntry[1])) {
                  if (p.replace(/\D/g, '') === pasNum) {
                    for (const [r, itList] of Object.entries(rMap)) {
                      if (r.replace(/\D/g, '') === racNum) {
                        itemsRack = itList;
                        break;
                      }
                    }
                  }
                }
              }
            }
            const cap = obtenerCapacidadRack({
              zonaOperativa: rackData.zona,
              pasillo: rackData.pasillo,
              rack: rackData.rack,
            });

            const codigoVisual = formatearCodigoUbicacion(
              {
                zonaOperativa: rackData.zona,
                pasillo: rackData.pasillo,
                rack: rackData.rack,
              },
              {
                incluirNivel: false,
                incluirPosicion: false,
                formato: 'visual',
              }
            );

            return (
              <Rack3D
                key={rackData.id}
                id={rackData.id}
                zona={rackData.zona}
                pasillo={rackData.pasillo}
                rack={rackData.rack}
                items={itemsRack}
                capacidad={{
                  ...cap,
                  niveles: rackData.niveles,
                  max_ton: rackData.max_ton,
                  max_unidades: rackData.max_unidades,
                }}
                codigoVisual={codigoVisual}
                position={[rackData.x, 0, rackData.z]}
                rotacion={rackData.rotacion}
                ancho={rackData.ancho}
                nivelesOverride={rackData.niveles}
                isSelected={modoArquitecto && rackSeleccionadoId === rackData.id}
                modoArquitecto={modoArquitecto}
                isModoReubicacion={Boolean(slotOrigenReubicacion)}
                slotOrigenReubicacionId={slotOrigenReubicacion?.slotKey}
                onSelectRack={onSelectRack}
                onSelectForEdit={(id) => setRackSeleccionadoId(id)}
                onSelectSlot={(slot) => {
                  if (slotOrigenReubicacion) {
                    if (slot.slotKey === slotOrigenReubicacion.slotKey) {
                      setSlotOrigenReubicacion(null);
                      toast('Reubicación cancelada');
                      return;
                    }
                    if (slot.isOccupied) {
                      toast.error('Este hueco ya está ocupado. Elige un slot libre.');
                      return;
                    }
                    handleEjecutarReubicacion(slotOrigenReubicacion, slot);
                  } else {
                    setSlotSeleccionado(slot);
                  }
                }}
              />
            );
          })}
        </group>
      </Canvas>
    </div>
  );
};
